// Global balance parameters ported from alphabet_forge_balance.md (Section 1).
// Single source of truth — tune here, never in component code.
export const GLOBAL = {
  // Letter scaling
  levelCostMultiplier: 1.11, // cost per level
  levelOutputMultiplier: 1.13, // output per level

  // Level soft cap (balance §8.1): beyond softCapLevel the cost multiplier
  // steepens so a single letter can't trivially outrun the economy.
  softCapLevel: 25,
  softCapCostMultiplier: 1.2,

  // Onboarding (Phase 0): a letter only produces passive Ink/s once it reaches
  // this level. Below it, the letter is "manual only" (contributes to tap).
  passiveUnlockLevel: 3,

  // Manual tap: fraction of one second of total passive output granted per tap,
  // with a small floor so the very first taps feel responsive.
  tapFractionOfOutput: 2.5,
  tapFloor: 2.0,

  // Capitalization
  capRitualCostMultiplier: 25, // x base unlock cost
  capOutputMultiplier: 12, // uppercase vs maxed lowercase

  // Prestige
  prestigeInkEdition1: 60000,
  prestigeGrowthPerEdition: 8,
  // NOTE: balance sheet states (Ink^0.5)/80 but its own table implies ~/25.2.
  // Using a constant that reproduces the table's "first reset ~10 Type".
  typeFromInk: (ink: number) => Math.sqrt(ink) / 25.2,

  // Offline progression (not in original docs — added per review).
  offlineCapHours: 8,
  offlineRate: 1.0, // full rate up to the cap

  // Operator unlock gates (distinct letters owned)
  operatorGates: [5, 9, 14, 18, 22, 26],

  // --- Forward-looking tunables for later milestones (balance §8) ---
  // Operator Edition gating (§8.9): which Edition enables each operator.
  operatorEditions: {
    '+': 1, '-': 1, '*': 1, '/': 2, '^': 2, '=': 3, '!': 3, '?': 3, '.': 3,
  } as Record<string, number>,
  multiplyScalar: 0.4, // × operator
  multiplyCapFactor: 5, // × output capped at (A+B)*this (§8.1)
  exponentPower: 1.15, // ^ operator
  exponentCapFactor: 6, // ^ output capped at A*this (§8.4)
  subtractTransfer: 1.8, // − operator transfer ratio (§8.3)

  // Generator board slots (§8.2)
  startingSlots: 6,
  slotsPerEdition: 1,

  // Word-burst governor "Quill Charge" (§8.5)
  quillChargeMax: 3,
  quillChargeRegenSeconds: 18,
  burstGlobalLockoutSeconds: 3,
  wordCooldownSeconds: 120,

  // Combo chain (§8.6)
  comboWindowSeconds: 12,
  comboBonusPerLink: 0.05,
  comboBonusCap: 0.5,

  // Word burst formula constants (§4)
  burstLengthScalar: 0.6,
  burstLengthExponent: 1.6,
  burstRareBonus: 0.35,

  // Anti-snowball caps (§8.8)
  setBonusEachMax: 0.05,
  setBonusTotalMax: 0.5,
  masteryGlobalMax: 0.25,
  globalMultiplierHardCap: 3,

  // Per-word levelling / mastery (§4.2)
  wordMaxLevel: 10,
  wordLevelMultPerLevel: 0.04, // +4% burst per word level above Lv1
  wordXpPerLevel: 3, // uses needed to advance a level = level * this
  masteryGlobalPerWord: 0.005, // +0.5% global per mastered word (capped at §8.8)
  dailyWordBonus: 3.0, // Daily Word: +300% to that word's own burst (§8.8: local only)

  // Forge Metrics (§2.5) — outside the ×3 global hard cap (§8.8)
  // Fresh Ink: type coating quality — decays over time, frequent letters refresh, scarce letters dry out.
  heatMin: -100,
  heatMax: 100,
  heatDecayBase: 0.01, // base drying rate (/s) — half-life ~69s at Edition 0
  heatDecayEditionScale: 0.5, // effectiveRate = base × (1 + edition × scale)
  forgeMultiplierScale: 200, // mult = 1 + ink/scale → ink=100 → +50%

  // Roller Pressure: impression force — no time decay, driven purely by letter usage.
  // Per-letter contribution scales with edition: ±(1 + edition) per letter.
  weightMin: -100,
  weightMax: 100,
  hammerMultiplierScale: 200, // mult = 1 + pressure/scale → pressure=100 → +50%

  // Length: accumulator for story-sentence unlocks.
  // Δlength = wordLen − lengthPivot; >5 adds, <5 subtracts, =5 neutral.
  lengthPivot: 5,
  sentenceLengthThreshold: 20, // first sentence unlocks after this total length (short for fast early progress)

  // Letter Mastery (Milestone 3)
  masteryWordsRequired: 10,  // Lexicographer milestone
  masterySwiftWindow: 15,    // Swift milestone: seconds

  // Typeface Tree (Milestone 4)
  typefaceBaseCost: 10,
  typefaceCostGrowth: 1.35,
  serifBonusPerTier: 0.15,  // +15% global output
  boldBurstPerTier: 0.10,   // +10% word burst
  italicCooldownReduction: 0.08, // -8% operator cooldown

  // Operator tuning (Milestone 4)
  addScalar: 1.0,
  divideCopyRatio: 0.55,
  equationBurstScalar: 8,
  equationCooldownSeconds: 30,
} as const

export type Rarity = 'Common' | 'Uncommon' | 'Rare' | 'Legend'
